import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LintChat } from "./LintChat";
import { LintBubble } from "./LintBubble";

const heroPills = [
  "Problem framing",
  "Data models",
  "Architecture & tech",
  "API contracts",
  "Deployment topology",
  "Security & auth",
  "Delivery plan",
  "Risk register",
];

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

export function Hero() {
  return (
    <section
      className="grain relative overflow-hidden bg-hero-mesh pb-16 pt-28 sm:pt-32 lg:min-h-[100svh] lg:pb-36"
      data-testid="hero"
    >
      <div className="relative mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <motion.p {...fade(0)} className="text-xs font-semibold uppercase tracking-[0.22em] text-brand">
            BootRise · The CTO layer for founders
          </motion.p>
          <motion.h1
            {...fade(0.08)}
            className="font-display mt-5 max-w-xl text-5xl font-semibold leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-7xl"
            data-testid="hero-title"
          >
            Meet <span className="italic text-brand">Lint.</span>
          </motion.h1>
          <motion.p {...fade(0.14)} className="font-display mt-3 text-2xl text-ink-muted sm:text-3xl">
            Looks soft. Thinks harder.
          </motion.p>
          <motion.p {...fade(0.2)} className="mt-5 max-w-md text-base leading-relaxed text-ink-muted">
            Your calm AI CTO. Tell him your idea and he&apos;ll challenge the
            assumption you didn&apos;t know you made. Two questions, free.
          </motion.p>
          <motion.div {...fade(0.28)} className="mt-7 max-w-xl">
            <LintChat />
          </motion.div>
          <motion.div {...fade(0.36)} className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
            <a
              href="#waitlist"
              className="rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-brand"
              data-testid="hero-book-demo-btn"
            >
              Book a demo
            </a>
            <Link
              to="/demo"
              className="group inline-flex w-fit items-center gap-2 text-sm font-semibold text-brand transition-colors hover:text-navy"
              data-testid="hero-demo-link"
            >
              Watch Lint review a sample project
              <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </motion.div>
        </div>
        <HeroLint />
      </div>
      <HeroMarquee />
    </section>
  );
}

function HeroLint() {
  const [waving, setWaving] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setWaving(false), 2600);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
      className="relative mx-auto w-full max-w-md"
      data-testid="hero-lint"
      data-pose={waving ? "wave" : "hero"}
    >
      <div className="absolute inset-x-8 bottom-6 h-10 rounded-[50%] bg-navy/15 blur-xl" aria-hidden />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="relative mx-auto w-[78%]"
      >
        <img src="/lint_hero.webp" alt="" aria-hidden className="invisible w-full" />
        <AnimatePresence initial={false}>
          <motion.img
            key={waving ? "wave" : "hero"}
            src={waving ? "/lint_wave.webp" : "/lint_hero.webp"}
            alt="Lint, the BootRise AI CTO, a soft green character with glasses"
            initial={{ opacity: 0 }}
            animate={waving ? { opacity: 1, rotate: [0, -4, 4, -4, 4, 0] } : { opacity: 1, rotate: 0 }}
            exit={{ opacity: 0 }}
            transition={waving ? { rotate: { duration: 1.6, delay: 0.5, ease: "easeInOut" } } : { duration: 0.4 }}
            className="absolute inset-0 h-full w-full select-none object-contain"
            style={{ transformOrigin: "50% 90%" }}
            draggable={false}
          />
        </AnimatePresence>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.5 }}
        className="absolute -top-2 right-0 max-w-[220px] sm:-right-4"
      >
        <LintBubble testId="hero-lint-bubble">
          {waving ? "Hi, I'm Lint. Nice to meet you." : <>Before we build, what problem are we <em>really</em> solving?</>}
        </LintBubble>
      </motion.div>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute left-2 top-16 rounded-xl border-2 border-sage px-2.5 py-1 font-mono text-lg font-bold text-brand"
        aria-hidden
      >
        &lt;/&gt;
      </motion.span>
      <p className="absolute -bottom-2 right-4 -rotate-6 font-hand text-2xl text-brand sm:text-3xl">
        Same idea. Better questions.
      </p>
    </motion.div>
  );
}

function HeroMarquee() {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 hidden lg:block" aria-hidden>
      <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-muted/70">
        What Lint thinks through before a line of code
      </p>
      <div className="relative overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_12%,black_88%,transparent)]">
        <div className="flex w-max gap-3 px-6 animate-marquee">
          {[...heroPills, ...heroPills].map((pill, i) => (
            <span
              key={i}
              className="whitespace-nowrap rounded-full border border-border bg-surface-elevated/80 px-4 py-2 text-sm font-medium text-navy"
            >
              {pill}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
